/**
 * Date helpers for the date-only strings (YYYY-MM-DD) and timestamps the app
 * stores.
 *
 * `new Date('2026-07-15')` parses as UTC midnight, which is 01:00 in Lisbon
 * summer time, so comparing it against a local "now" can flag an invoice due
 * today as already overdue. Everything here works in local time at the start
 * of the day, and formats back with local getters rather than `slice(0, 10)`
 * on a UTC string, which shifts late-evening timestamps to the previous day.
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function todayStart(): Date {
  return startOfDay(new Date());
}

/** Local midnight for a date-only string; start of local day for a timestamp. */
export function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = DATE_ONLY.exec(value.trim());
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : startOfDay(d);
}

/** Whole days from `a` to `b`, positive when `b` is later. */
export function daysBetween(a: Date, b: Date): number {
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / 86_400_000);
}

/** YYYY-MM-DD in local time, for date inputs and month bucketing. */
export function toLocalDateOnly(value: Date | string | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : parseDateOnly(value);
  if (!d || isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** True only once the due date is strictly in the past — due today is on time. */
export function isPastDue(dateStr: string | null | undefined, today: Date = todayStart()): boolean {
  const due = parseDateOnly(dateStr);
  return !!due && due.getTime() < startOfDay(today).getTime();
}

/** Days overdue (0 when not yet due). */
export function daysOverdue(dateStr: string | null | undefined, today: Date = todayStart()): number {
  const due = parseDateOnly(dateStr);
  if (!due) return 0;
  return Math.max(0, daysBetween(due, today));
}

/**
 * The date a campaign is "about" for payment views: the delivery date for
 * creator work, or the event date for sponsorships, which never have one.
 */
export function getCampaignReferenceDate(campaign: {
  delivery_date?: string | null;
  event_date?: string | null;
  event_end_date?: string | null;
}): string | null {
  return campaign.delivery_date || campaign.event_date || null;
}
